import type { AfterViewInit, OnChanges, OnInit } from '@angular/core';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import type { UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';

@Component({
  standalone: false,
  selector: 'invd-fm-input-with-validations',
  templateUrl: './fm-input-with-validations.component.html',
  styleUrls: ['./fm-input-with-validations.component.scss'],
})
export class FmInputWithValidationsComponent implements OnInit, AfterViewInit, OnChanges {
  @Input()
  requestType: string;

  @Output()
  dealSearch = new EventEmitter<any>();

  public usrInput: string;

  public hint: string;

  public errorMsg = '';

  public form: UntypedFormGroup;

  public isSearchBtnEnable = false;

  //for demo use only
  public mDealSubType = 'SFREMIC';

  public fb = inject(UntypedFormBuilder);

  ngOnInit() {
    this.form = this.fb.group({
      dealNbr: new UntypedFormControl('', [Validators.required, this.validateSearchNo()]),
    });
  }

  ngAfterViewInit() {
    this.onInitTooltipError();
  }

  ngOnChanges() {
    this.validateSearchNo();
    this.updateHintText();
  }

  public onDealSearch(): void {
    this.dealSearch.emit({ dealSubType: this.mDealSubType, userSearchInput: this.usrInput });
    if (!this.form.get('dealNbr').invalid) {
      this.dealSearch.emit({ dealSubType: this.mDealSubType, userSearchInput: this.usrInput });
    }
  }

  private validateSearchNo(): ValidatorFn {
    function isEmptyInputValue(value: any) {
      return value == null || (typeof value === 'string' && value.length === 0);
    }

    return (control: UntypedFormControl): { [key: string]: boolean } => {
      if (isEmptyInputValue(control.value)) {
        return null;
      }

      const cusipRegex = new RegExp('^[a-zA-Z0-9]{9}$');
      if (this.requestType === 'Single Family REMIC') {
        this.mDealSubType = 'SFREMIC';

        const dealRegex = new RegExp('^[0-9]{4}[-][0-9]{3}$');
        if (dealRegex.test(control.value) || cusipRegex.test(control.value)) {
          this.isSearchBtnEnable = true;
          return { sfRemicError: false };
        } else {
          this.isSearchBtnEnable = false;
          return { sfRemicError: true };
        }
      } else if (this.requestType === 'SMBS') {
        this.mDealSubType = 'SMBS';
        const dealRegex = new RegExp('^[0-9]{3}$');
        if (dealRegex.test(control.value) || cusipRegex.test(control.value)) {
          this.isSearchBtnEnable = true;
          return { smbsError: false };
        } else {
          return { smbsError: true };
        }
      } else if (this.requestType === 'Multi Family REMIC') {
        this.mDealSubType = 'MFREMIC';
        const dealRegex = new RegExp('^[0-9]{4}[-][Mm][0-9A-Za-z]{2}$');
        if (dealRegex.test(control.value) || cusipRegex.test(control.value)) {
          this.isSearchBtnEnable = true;
          return { mfRemicError: false };
        } else {
          this.isSearchBtnEnable = false;
          if (this.usrInput) {
            if (this.usrInput.length > 3) {
              return { mfRemicError: true };
            } else {
              return { mfRemicError: false };
            }
          }
        }
      } else if (this.requestType === 'Benchmark REMIC') {
        this.mDealSubType = 'BMREMIC';
        const dealRegex = new RegExp('^[0-9]{4}[-][B][0-9]{2}$');
        if (dealRegex.test(control.value) || cusipRegex.test(control.value)) {
          this.isSearchBtnEnable = true;
          return { bmRemicError: false };
        } else {
          this.isSearchBtnEnable = false;
          if (this.usrInput) {
            if (this.usrInput.length > 3) {
              return { bmRemicError: true };
            } else {
              return { bmRemicError: false };
            }
          }
        }
      } else {
        this.isSearchBtnEnable = true;
      }
    };
  }

  private updateHintText(): void {
    if (this.requestType != null) {
      switch (this.requestType) {
        case 'Single Family REMIC': {
          this.hint = 'Enter Deal (ex.2016-001) or CUSIP';
          break;
        }
        case 'SMBS': {
          this.hint = 'Enter Trust (ex. 100) or CUSIP';
          break;
        }
        case 'Multi Family REMIC': {
          this.hint = 'Enter Deal (ex.2016-M01) or CUSIP';
          break;
        }
        case 'Benchmark REMIC': {
          this.hint = 'Enter Deal (ex.2016-B01) or CUSIP';
          break;
        }
        default: {
          this.hint = 'Enter Deal No.(ex.2016-74) or CUSIP';
          break;
        }
      }

      this.isSearchBtnEnable = false;
    }
  }

  private onInitTooltipError(): void {}
}
